(function () {
  'use strict';

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  function mobileMedia() {
    return window.matchMedia('(max-width: 989px)');
  }

  function clearGsapProps(panel) {
    if (!panel) return;
    if (window.gsap) {
      window.gsap.killTweensOf(panel);
      window.gsap.set(panel, { clearProps: 'opacity,visibility,transform,pointerEvents' });
    } else {
      panel.style.opacity = '';
      panel.style.visibility = '';
      panel.style.transform = '';
      panel.style.pointerEvents = '';
    }
  }

  var menuControllers = new WeakMap();
  var menuSequence = 0;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var refreshFrame = null;

  function refreshScrollLayout() {
    if (!window.ScrollTrigger) return;
    window.cancelAnimationFrame(refreshFrame);
    refreshFrame = window.requestAnimationFrame(function () {
      window.ScrollTrigger.refresh();
    });
  }

  function initMenuAccordion(group) {
    if (menuControllers.has(group)) return;
    var summary = group.querySelector('.tajima-sidebar__menu-summary');
    var content = group.querySelector('.tajima-sidebar__menu-panel');
    if (!summary || !content) return;

    var expanded = group.open;
    var heightAnimation = null;
    var fadeAnimation = null;
    if (!content.id) {
      do {
        menuSequence += 1;
        content.id = 'tajima-sidebar-menu-panel-' + menuSequence;
      } while (document.getElementById(content.id) !== content);
    }
    summary.setAttribute('aria-controls', content.id);
    summary.setAttribute('aria-expanded', String(expanded));
    content.toggleAttribute('inert', !expanded);

    function cancelAnimations() {
      if (heightAnimation) {
        heightAnimation.onfinish = null;
        heightAnimation.cancel();
        heightAnimation = null;
      }
      if (fadeAnimation) {
        fadeAnimation.cancel();
        fadeAnimation = null;
      }
    }

    function finish() {
      group.open = expanded;
      cancelAnimations();
      content.toggleAttribute('inert', !expanded);
      summary.setAttribute('aria-expanded', String(expanded));
      refreshScrollLayout();
    }

    function setExpanded(next) {
      var fromHeight = group.getBoundingClientRect().height;
      var fromOpacity = group.open ? window.getComputedStyle(content).opacity : '0';
      cancelAnimations();
      expanded = next;
      summary.setAttribute('aria-expanded', String(expanded));

      if (reducedMotion.matches || typeof group.animate !== 'function') {
        finish();
        return;
      }

      group.open = true;
      var styles = window.getComputedStyle(group);
      var edges = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth) +
        parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      var toHeight = expanded ? group.getBoundingClientRect().height :
        summary.getBoundingClientRect().height + edges;
      content.toggleAttribute('inert', !expanded);

      if (Math.abs(toHeight - fromHeight) < 0.5) {
        finish();
        return;
      }

      var duration = expanded ? 340 : 280;
      heightAnimation = group.animate([
        { height: fromHeight + 'px' },
        { height: toHeight + 'px' }
      ], {
        duration: duration,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both'
      });
      fadeAnimation = content.animate([
        { opacity: fromOpacity },
        { opacity: expanded ? 1 : 0 }
      ], {
        duration: duration,
        easing: 'ease-out',
        fill: 'both'
      });
      heightAnimation.onfinish = finish;
    }

    function onClick(event) {
      if (event.target.closest('a')) return;
      event.preventDefault();
      setExpanded(!expanded);
    }

    function onToggle() {
      if (heightAnimation) return;
      expanded = group.open;
      content.toggleAttribute('inert', !expanded);
      summary.setAttribute('aria-expanded', String(expanded));
    }

    function onResize() {
      if (heightAnimation) finish();
    }

    function onMotionChange() {
      if (reducedMotion.matches && heightAnimation) finish();
    }

    summary.addEventListener('click', onClick);
    group.addEventListener('toggle', onToggle);
    window.addEventListener('resize', onResize);
    reducedMotion.addEventListener('change', onMotionChange);
    menuControllers.set(group, {
      destroy: function () {
        finish();
        summary.removeEventListener('click', onClick);
        group.removeEventListener('toggle', onToggle);
        window.removeEventListener('resize', onResize);
        reducedMotion.removeEventListener('change', onMotionChange);
        menuControllers.delete(group);
      }
    });
  }

  function initSidebar(sidebar) {
    if (!sidebar) return;
    sidebar.querySelectorAll('.tajima-sidebar__menu-group').forEach(initMenuAccordion);
    if (sidebar.dataset.tajimaSidebarReady === 'true') return;
    sidebar.dataset.tajimaSidebarReady = 'true';

    var toggle = sidebar.querySelector('[data-sidebar-toggle]');
    var close = sidebar.querySelector('[data-sidebar-close]');
    var overlay = sidebar.querySelector('[data-sidebar-overlay]');
    var panel = sidebar.querySelector('.tajima-sidebar__panel');

    var mq = mobileMedia();
    var isAnimating = false;

    if (overlay) {
      overlay.setAttribute('aria-hidden', 'true');
    }

    function setExpanded(value) {
      if (toggle) toggle.setAttribute('aria-expanded', value ? 'true' : 'false');
    }

    function setOverlayOpen(value) {
      if (!overlay) return;
      overlay.setAttribute('aria-hidden', value ? 'false' : 'true');
    }

    function openSidebar() {
      if (!mq.matches || sidebar.classList.contains('is-open') || isAnimating) return;
      sidebar.classList.add('is-open');
      document.body.classList.add('tajima-sidebar-open');
      setOverlayOpen(true);
      setExpanded(true);

      if (window.gsap && panel && !reducedMotion.matches) {
        isAnimating = true;
        window.gsap.killTweensOf(panel);
        window.gsap.fromTo(
          panel,
          { autoAlpha: 0, y: -8 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.28,
            ease: 'power2.out',
            onComplete: function () {
              isAnimating = false;
            }
          }
        );
      }
    }

    function closeSidebar(immediate) {
      if (!sidebar.classList.contains('is-open')) {
        setExpanded(false);
        return;
      }

      function finish() {
        sidebar.classList.remove('is-open');
        document.body.classList.remove('tajima-sidebar-open');
        setOverlayOpen(false);
        setExpanded(false);
        isAnimating = false;
        clearGsapProps(panel);
      }

      if (immediate || reducedMotion.matches || !window.gsap || !panel) {
        finish();
        return;
      }

      isAnimating = true;
      window.gsap.killTweensOf(panel);
      window.gsap.to(panel, {
        autoAlpha: 0,
        y: -8,
        duration: 0.22,
        ease: 'power2.inOut',
        onComplete: finish
      });
    }

    function syncViewport() {
      if (!mq.matches) {
        closeSidebar(true);
        clearGsapProps(panel);
      }
    }

    if (toggle) {
      toggle.addEventListener('click', function () {
        if (sidebar.classList.contains('is-open')) {
          closeSidebar(false);
        } else {
          openSidebar();
        }
      });
    }

    if (close) {
      close.addEventListener('click', function () {
        closeSidebar(false);
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeSidebar(false);
    });

    if (overlay) {
      overlay.addEventListener('click', function () {
        closeSidebar(false);
      });
    }

    if (mq.addEventListener) {
      mq.addEventListener('change', syncViewport);
    } else if (mq.addListener) {
      mq.addListener(syncViewport);
    }

    syncViewport();
  }

  function init(root) {
    if (root.matches && root.matches('[data-tajima-sidebar]')) initSidebar(root);
    root.querySelectorAll('[data-tajima-sidebar]').forEach(initSidebar);
  }

  ready(function () { init(document); });
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('.tajima-sidebar__menu-group').forEach(function (group) {
      var controller = menuControllers.get(group);
      if (controller) controller.destroy();
    });
  });
})();
